import List from '@pelckmans/elementis/components/list';
import { useTranslation } from 'react-i18next';
import { ContentType } from '../../../components/MaterialIcon';
import useScorableItemSelection from './hooks/useScorableItemSelection';
import { ComponentProps, useEffect, useState } from 'react';
import { ScorableItemTableDataGroup } from '../per-student/hooks/useTableData';

interface ScorableItemBase {
  id: string;
  materialId: string;
  title: string;
  materialType: 'user-material' | 'medialink';
  scorableItemType: 'free-exercise' | 'assignment';
  contentType?: ContentType;
  level: number;
  nodeId: string;
}

interface AssignmentScorableItem extends ScorableItemBase {
  scorableItemType: 'assignment';
  assignmentRanges: { from: string; to: string }[];
}

interface FreeExerciseScorableItem extends ScorableItemBase {
  scorableItemType: 'free-exercise';
}

export type ScorableItem = AssignmentScorableItem | FreeExerciseScorableItem;

export interface RowGroup {
  id: string;
  title: string;
  prefix?: string;
  imageUrl?: string;
  level: number;
  nodes?: RowGroup[];
  parentNodeId: string | undefined;
  scorableItems?: ScorableItem[];
}

type Level = NonNullable<ComponentProps<typeof List.Item>['level']>;

interface Props
  extends Pick<
    ReturnType<typeof useScorableItemSelection>,
    'getCheckboxState' | 'setNodeCheckboxState' | 'setScorableItemCheckboxState'
  > {
  tableDataGroup: ScorableItemTableDataGroup;
  filterActive?: boolean;
}

export default function ScorableItemRowGroup({
  filterActive,
  tableDataGroup,
  getCheckboxState,
  setNodeCheckboxState,
  setScorableItemCheckboxState,
}: Props) {
  const { id, level, subTitleTop, image } = tableDataGroup.accumulatedRow;

  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(!filterActive && level === 0);

  useEffect(() => {
    if (filterActive === undefined) return;

    setCollapsed(!filterActive && level === 0);
  }, [filterActive, level]);

  let { title } = tableDataGroup.accumulatedRow;
  if (id === 'general') {
    title = t('TOC.GENERAL');
  }

  return (
    <>
      <List.Item
        as="checkbox"
        onClick={() => {
          const currentState = getCheckboxState(id);
          setNodeCheckboxState(id, currentState === 'checked' ? 'unchecked' : 'checked');
        }}
        checked={getCheckboxState(id) === 'checked'}
        // avoids error for passing boolean to non-boolean prop
        indeterminate={getCheckboxState(id) === 'indeterminate' ? true : false}
        title={title}
        subText={subTitleTop}
        subTextPlacement="top"
        avatar={
          image
            ? {
                src: image,
                name: t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.NODE_ITEMS.AVATAR_A11Y_LABEL', {
                  nodeTitle: title,
                }),
                title,
              }
            : undefined
        }
        level={level as Level}
        key={id}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        collapseButtonLabelText={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.NODE_ITEMS.COLLAPSE_BUTTON.A11Y_LABEL')}
        data-testid={`node-${id}`}
      />
      {!collapsed &&
        tableDataGroup.rows?.map(item => (
          <List.Item
            as="checkbox"
            checked={getCheckboxState(item.id) === 'checked'}
            onClick={() => {
              const currentState = getCheckboxState(item.id);
              setScorableItemCheckboxState(item.id, currentState === 'checked' ? 'unchecked' : 'checked');
            }}
            title={item.title}
            subText={t(`PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.TYPE_SELECT.${item.type.toUpperCase()}`)}
            icon={item.icon}
            level={item.level as Level}
            key={item.id}
            data-testid={`scorable-item-${item.id}`}
          />
        ))}
      {!collapsed && tableDataGroup.groups?.length
        ? tableDataGroup.groups.map(childGroup => (
            <ScorableItemRowGroup
              tableDataGroup={childGroup}
              getCheckboxState={getCheckboxState}
              setNodeCheckboxState={setNodeCheckboxState}
              setScorableItemCheckboxState={setScorableItemCheckboxState}
              key={childGroup.id}
            />
          ))
        : null}
    </>
  );
}
